import { RefObject, useLayoutEffect, useState } from 'react';
import { paginateRows } from '../utils/paginateRows';

export type ReportSheet = {
  /** Indexes into the full row list that belong on this sheet. */
  rowIndexes: number[];
  /** Carries the footer blocks. */
  hasFooter: boolean;
  /** Nothing but the footer fitted here. */
  isFooterOnly: boolean;
};

export type SheetPaginationState = {
  sheets: ReportSheet[] | undefined;
  /** True until the measure pass has run for the current content. */
  isMeasuring: boolean;
};

interface UseSheetPaginationParams {
  /** Wrapper of the offscreen measure pass. */
  measureRef: RefObject<HTMLDivElement | null>;
  /** Changes whenever anything affecting heights changes, forcing a re-measure. */
  measureKey: string;
  rowCount: number;
  printableHeightPx: number;
  paginationReserveHeight: number;
  hasFooter: boolean;
}

/**
 * Measures the rendered report once, then splits it into sheets.
 *
 * Heights are read from the DOM rather than estimated from the font metrics: a
 * row wraps to two lines whenever a value is long, and an estimate would put the
 * page break in the wrong place. Two passes — measure offscreen at the real
 * printable width, then lay out the sheets.
 */
export const useSheetPagination = ({
  measureRef,
  measureKey,
  rowCount,
  printableHeightPx,
  paginationReserveHeight,
  hasFooter,
}: UseSheetPaginationParams): SheetPaginationState => {
  const [state, setState] = useState<{ key: string; sheets: ReportSheet[] }>();

  useLayoutEffect(() => {
    const node = measureRef.current;
    if (!node) {
      return;
    }

    const header = node.querySelector<HTMLElement>('.report-section-header');
    const footer = node.querySelector<HTMLElement>('.report-section-footer');
    const emptyNotice = node.querySelector<HTMLElement>('.report-empty');
    const headRow =
      node.querySelector<HTMLElement>(
        '.report-items-primary-heading .report-items-table thead tr'
      ) ?? node.querySelector<HTMLElement>('.report-items-table thead tr');
    const groups = Array.from(
      node.querySelectorAll<HTMLElement>('.report-item-group')
    );
    const bodyRows =
      groups.length > 0
        ? groups
        : Array.from(
            node.querySelectorAll<HTMLElement>('.report-items-table tbody tr')
          );

    // Subpixel heights, not offsetHeight: offsetHeight rounds to whole pixels
    // and over a couple of dozen rows the rounding accumulates into a real
    // overflow past the bottom of the sheet.
    const heightOf = (element: HTMLElement | null): number =>
      element ? element.getBoundingClientRect().height : 0;

    const { pages, footerOnOwnPage } = paginateRows({
      rowHeights: bodyRows.map(heightOf),
      contentHeight: printableHeightPx,
      headerHeight: heightOf(header),
      theadHeight: heightOf(headRow),
      footerHeight: heightOf(footer),
      emptyNoticeHeight: heightOf(emptyNotice),
      paginationReserveHeight,
    });

    const sheets: ReportSheet[] = pages.map((rowIndexes, index) => ({
      rowIndexes,
      hasFooter:
        hasFooter && !footerOnOwnPage && index === pages.length - 1,
      isFooterOnly: false,
    }));

    if (hasFooter && footerOnOwnPage) {
      sheets.push({ rowIndexes: [], hasFooter: true, isFooterOnly: true });
    }

    setState((previous) =>
      previous?.key === measureKey ? previous : { key: measureKey, sheets }
    );
    // measureRef is a stable ref object, so listing it cannot re-trigger the pass.
  }, [
    measureRef,
    measureKey,
    printableHeightPx,
    paginationReserveHeight,
    hasFooter,
    rowCount,
  ]);

  return {
    sheets: state?.key === measureKey ? state.sheets : undefined,
    isMeasuring: state?.key !== measureKey,
  };
};
